import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import {
  cleanupDueReviewImages,
  finalizeReviewImageModeration,
  isDualReviewImage,
  loadOriginalReviewImagePreview,
  selectReviewImageVariant,
} from "./reviewImageModeration";

const shell = {
  position: "fixed",
  inset: 0,
  zIndex: 10050,
  background: "rgba(0,0,0,.82)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 14,
};
const panel = {
  width: "min(920px, 100%)",
  maxHeight: "92vh",
  overflowY: "auto",
  background: "#0c0c0c",
  color: "#fff",
  border: "1px solid #2b2b2b",
  borderRadius: 16,
  padding: 16,
};
const imageBox = {
  flex: "1 1 260px",
  minWidth: 0,
  border: "1px solid #292929",
  borderRadius: 12,
  padding: 10,
  background: "#121212",
};
const button = {
  border: 0,
  borderRadius: 10,
  padding: "9px 12px",
  cursor: "pointer",
  fontWeight: 700,
};

const keyFor = (reviewId, pairId) => `${reviewId}:${pairId}`;

export default function AdminReviewImageModerationLayer() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [selections, setSelections] = useState({});
  const [previews, setPreviews] = useState({});
  const [comments, setComments] = useState({});

  const loadPending = useCallback(async () => {
    const { data, error } = await supabase
      .from("reviews")
      .select("id,username,placeid,imagenes,pending,moderation_status")
      .eq("pending", true)
      .eq("moderation_status", "pending");
    if (error) return;
    const dual = (data || []).filter(review =>
      (Array.isArray(review.imagenes) ? review.imagenes : []).some(isDualReviewImage),
    );
    setReviews(dual);
    setSelections(previous => {
      const next = { ...previous };
      dual.forEach(review => {
        (review.imagenes || []).filter(isDualReviewImage).forEach(image => {
          const persisted = image?.moderation?.selection;
          if (persisted) next[keyFor(review.id, image.moderation.pair_id)] = persisted;
        });
      });
      return next;
    });
  }, []);

  const verify = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session?.user?.id) {
      setIsAdmin(false);
      setReviews([]);
      return;
    }
    const { data, error } = await supabase.rpc("is_master_admin");
    const allowed = !error && data === true;
    setIsAdmin(allowed);
    if (allowed) {
      await cleanupDueReviewImages(20).catch(() => null);
      await loadPending();
    }
  }, [loadPending]);

  useEffect(() => {
    verify();
    const { data } = supabase.auth.onAuthStateChange(() => setTimeout(verify, 0));
    return () => data?.subscription?.unsubscribe?.();
  }, [verify]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const timer = setInterval(() => cleanupDueReviewImages(20).catch(() => null), 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, [isAdmin]);

  const pendingCount = useMemo(
    () => reviews.reduce((sum, review) => sum + (review.imagenes || []).filter(
      image => isDualReviewImage(image) && !image?.moderation?.selection,
    ).length, 0),
    [reviews],
  );

  if (!isAdmin || reviews.length === 0) return null;

  const loadOriginal = async (review, image) => {
    const key = keyFor(review.id, image.moderation.pair_id);
    if (previews[key]) return;
    setBusy(`preview:${key}`);
    setMessage("");
    try {
      const url = await loadOriginalReviewImagePreview(image?.moderation?.original?.asset_id);
      setPreviews(previous => ({ ...previous, [key]: url }));
    } catch (error) {
      setMessage(error?.message || "No se pudo cargar el original.");
    } finally {
      setBusy("");
    }
  };

  const publishReview = async review => {
    const dualImages = (review.imagenes || []).filter(isDualReviewImage);
    const unresolved = dualImages.filter(image => {
      const key = keyFor(review.id, image.moderation.pair_id);
      return !image?.moderation?.selection && !["original", "protected"].includes(selections[key]);
    });
    if (unresolved.length) {
      setMessage("Elige Original o Protegida para cada foto antes de publicar.");
      return;
    }

    setBusy(`publish:${review.id}`);
    setMessage("");
    try {
      for (const image of dualImages) {
        if (image?.moderation?.selection) continue;
        const key = keyFor(review.id, image.moderation.pair_id);
        await selectReviewImageVariant(review.id, image.moderation.pair_id, selections[key]);
      }
      await finalizeReviewImageModeration(review.id, comments[review.id] || "");
      setMessage("Reseña publicada. La variante descartada queda programada para borrado en 48 h.");
      await loadPending();
    } catch (error) {
      setMessage(error?.message || "No se pudo publicar la reseña.");
      await loadPending();
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          ...button,
          position: "fixed",
          left: 12,
          bottom: 74,
          zIndex: 10040,
          background: "#e53935",
          color: "#fff",
          boxShadow: "0 8px 28px rgba(0,0,0,.4)",
        }}
      >
        Fotos por validar {pendingCount ? `(${pendingCount})` : ""}
      </button>

      {open && (
        <div style={shell}>
          <div style={panel} role="dialog" aria-modal="true" aria-label="Moderación de fotos">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20 }}>Moderación de fotos</h2>
                <p style={{ margin: "5px 0 0", color: "#aaa", fontSize: 12 }}>
                  Compara el original privado con la versión protegida. Sólo la variante elegida se publica.
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} style={{ ...button, background: "#222", color: "#fff" }}>Cerrar</button>
            </div>

            {message && (
              <div style={{ marginTop: 12, padding: 10, borderRadius: 10, background: "#1a1a1a", fontSize: 13 }}>
                {message}
              </div>
            )}

            {reviews.map(review => {
              const dualImages = (review.imagenes || []).filter(isDualReviewImage);
              return (
                <section key={review.id} style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #242424" }}>
                  <strong>Reseña de {review.username || "usuario"} · Lugar #{review.placeid}</strong>

                  {dualImages.map((image, index) => {
                    const pairId = image.moderation.pair_id;
                    const key = keyFor(review.id, pairId);
                    const chosen = image.moderation.selection || selections[key] || "";
                    const originalUrl = previews[key];
                    const protectedUrl = image?.moderation?.protected?.url || image.url;
                    return (
                      <div key={pairId} style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, color: "#aaa", marginBottom: 7 }}>Foto {index + 1}</div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <label style={imageBox}>
                            <div style={{ fontWeight: 700, marginBottom: 7 }}>Original</div>
                            {originalUrl ? (
                              <img src={originalUrl} alt="Original privado" style={{ width: "100%", maxHeight: 310, objectFit: "contain", borderRadius: 8, background: "#000" }} />
                            ) : (
                              <button
                                type="button"
                                disabled={busy === `preview:${key}`}
                                onClick={() => loadOriginal(review, image)}
                                style={{ ...button, width: "100%", minHeight: 110, background: "#222", color: "#fff" }}
                              >
                                {busy === `preview:${key}` ? "Cargando…" : "Ver original privado"}
                              </button>
                            )}
                            <div style={{ marginTop: 8 }}>
                              <input
                                type="radio"
                                name={key}
                                checked={chosen === "original"}
                                disabled={Boolean(image.moderation.selection)}
                                onChange={() => setSelections(previous => ({ ...previous, [key]: "original" }))}
                              />{" "}
                              Publicar original
                            </div>
                          </label>

                          <label style={imageBox}>
                            <div style={{ fontWeight: 700, marginBottom: 7 }}>Protegida</div>
                            <img src={protectedUrl} alt="Versión protegida" style={{ width: "100%", maxHeight: 310, objectFit: "contain", borderRadius: 8, background: "#000" }} />
                            <div style={{ marginTop: 8 }}>
                              <input
                                type="radio"
                                name={key}
                                checked={chosen === "protected"}
                                disabled={Boolean(image.moderation.selection)}
                                onChange={() => setSelections(previous => ({ ...previous, [key]: "protected" }))}
                              />{" "}
                              Publicar protegida
                            </div>
                          </label>
                        </div>
                      </div>
                    );
                  })}

                  <textarea
                    value={comments[review.id] || ""}
                    maxLength={150}
                    onChange={event => setComments(previous => ({ ...previous, [review.id]: event.target.value }))}
                    placeholder="Comentario administrativo opcional"
                    style={{
                      width: "100%",
                      minHeight: 60,
                      marginTop: 12,
                      boxSizing: "border-box",
                      borderRadius: 10,
                      border: "1px solid #2a2a2a",
                      background: "#151515",
                      color: "#fff",
                      padding: 10,
                    }}
                  />

                  <button
                    type="button"
                    disabled={busy === `publish:${review.id}`}
                    onClick={() => publishReview(review)}
                    style={{ ...button, marginTop: 8, background: "#e53935", color: "#fff" }}
                  >
                    {busy === `publish:${review.id}` ? "Publicando…" : "Aprobar y publicar selección"}
                  </button>
                </section>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
